import { useContext, useState } from "react"
import "bootstrap"
import getURLParams from "../helpers/getURLParams"
import removeMessageQuery from "../helpers/removeMessageQuery"
import LogoutWarning from "./Logout/LogoutWarning"
import TranslateWidget from "./General/TranslateWidget"
import { logout } from "shared/routes/auth"
import { toast } from "react-toastify"
import { useNavigate } from "react-router"
import { AuthContext } from "AuthContext"
import styles from "../styles/reusable.module.scss"
import ToolTip from "./General/ToolTip"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserAstronaut } from "@fortawesome/free-solid-svg-icons"
import { GuardedAnchor } from "./General/GuardedAnchor"
import { BlockType } from "shared/types/science35Types"

type Props = {
    isLoggedIn: boolean
    isStudent: boolean
    isAdmin: boolean
    isStaff: boolean
    trackingEnabled: boolean
    project: string
    hasStemSimsAccess: boolean
    hasCurriculumAccess: boolean
    scienceBlocks?: BlockType[]
}

export default function Header({ project, isLoggedIn, isAdmin, isStaff, isStudent, trackingEnabled, hasStemSimsAccess, hasCurriculumAccess, scienceBlocks }: Props) {
    const [ params ] = useState<{ success?: string, error?: string, info?: string }>(getURLParams())
    const cleanedUrl = new URL(removeMessageQuery(window.location.href))
    window.history.replaceState({}, "", cleanedUrl.href)

    const editing = process.env.REACT_APP_EDITING === "true"
    const [ showTranslateWidget, setShowTranslateWidget ] = useState(false)
    const navigate = useNavigate()
    const { triggerAuthRefresh, studentInfo } = useContext(AuthContext)

    const logOut = async (e) => {
        e.preventDefault()
        logout().then(res => {
            triggerAuthRefresh()
            navigate(res.data.url)
            return toast.success("You have successfully logged out.")
        })
    }

    const shouldGuardLinks = isStudent && cleanedUrl.pathname === "/dashboard/student/assignments/assignment"
    const showAssignmentsLink = isStudent && trackingEnabled && (hasStemSimsAccess || hasCurriculumAccess || scienceBlocks?.length > 0)
    const scienceOnlyAccess = !hasStemSimsAccess && !hasCurriculumAccess && scienceBlocks?.length > 0
    const shouldShowSubscribe = cleanedUrl.pathname.startsWith("/product") || cleanedUrl.pathname.startsWith("/homeschool")

    return <div className="position-sticky top-0" style={{ zIndex: 800 }}>
        <LogoutWarning isLoggedIn={isLoggedIn} />
        <nav className="navbar navbar-expand-md navbar-light shadow-sm bg-primary fixed-top px-1 pt-0 pb-0 d-print-none d-flex flex-wrap" >
            <div className="navbar-image navbar-brand navbar-brand-default ps-5 py-1 ">
                <GuardedAnchor href="/" shouldGuard={shouldGuardLinks}>
                    <i className="fa-solid fa-house" style={{color: "white", fontSize:"2rem"}}/>
                </GuardedAnchor>
            </div>
            {/* <button className="navbar-toggler position-absolute top-0 mt-2 d-md-none d-flex align-items-center" type="button" data-bs-toggle="collapse" data-bs-target="#navbarContent" aria-controls="navbarContent" aria-expanded="false" aria-label="Toggle navigation">
                <img
                    src="/images/logo.png"
                    alt={`${project} Logo`}
                    className="d-none d-sm-block" 
                    width="30"
                    height="30" />
                <span className="mx-2 d-none d-sm-block">STEM Sims</span>
                <span className="navbar-toggler-icon"></span>
            </button> */}

            <div className="collapse navbar-collapse flex-column align-items-start justify-content-start text-start order-1 order-md-0" id="navbarContent">
                <div className="pb-3 pb-md-0 px-5 px-md-0 ms-auto">
                    <ul className="navbar-nav">
                        <li className="nav-item d-md-none mt-3">
                            <GuardedAnchor href="/" shouldGuard={shouldGuardLinks} className="nav-link">Home</GuardedAnchor>
                        </li>
                        {!isLoggedIn && 
                            <li className="nav-item">
                                <GuardedAnchor href="/" shouldGuard={shouldGuardLinks} className="nav-link">Products</GuardedAnchor>
                            </li>
                        }
                        {isLoggedIn && !(isStudent && !hasStemSimsAccess) && (
                            <li className="nav-item">
                                <GuardedAnchor href="/lessons/search" shouldGuard={shouldGuardLinks} className="nav-link">Simulations</GuardedAnchor>
                            </li>
                        )}
                        {isLoggedIn && !isStudent && trackingEnabled && (
                            <li className="nav-item">
                                <GuardedAnchor href="/dashboard/teacher" shouldGuard={shouldGuardLinks} className="nav-link">Classes</GuardedAnchor>
                            </li>
                        )}
                        {isLoggedIn && isStudent && trackingEnabled && showAssignmentsLink && (
                            <li className="nav-item">
                                <GuardedAnchor
                                    href={scienceOnlyAccess ? "/dashboard/student/reviews" : "/dashboard/student/assignments"}
                                    shouldGuard={shouldGuardLinks}
                                    className="nav-link"
                                >
                                    {scienceOnlyAccess ? "Core Ideas" : "Assignments"}
                                </GuardedAnchor>
                            </li>
                        )}
                        {isAdmin && (
                            <li className="nav-item">
                                <GuardedAnchor href="/dashboard/district" shouldGuard={shouldGuardLinks} className="nav-link">Analytics</GuardedAnchor>
                            </li>
                        )}
                        {isStaff && (
                            <li className="nav-item">
                                <GuardedAnchor href="/admin" shouldGuard={shouldGuardLinks} className="nav-link">Admin</GuardedAnchor>
                            </li>
                        )}
                        {(hasCurriculumAccess) && (
                            <li className="nav-item">
                                <GuardedAnchor href="/books" shouldGuard={shouldGuardLinks} className="nav-link">Textbooks</GuardedAnchor>
                            </li>
                        )}
                        {editing && (
                            <li className="nav-item">
                                <a href="/editing" className="nav-link">Editing</a>
                            </li>
                        )}
                        {shouldShowSubscribe && !isLoggedIn && !isStudent && !trackingEnabled && (
                            <li className="nav-item">
                                <GuardedAnchor href="/subscribe" shouldGuard={shouldGuardLinks} className="nav-link">Subscribe</GuardedAnchor>
                            </li>
                        )}
                        {isLoggedIn && !isStudent && (
                            <li className="nav-item">
                                <GuardedAnchor href="/account" shouldGuard={shouldGuardLinks} className="nav-link">Account</GuardedAnchor>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
            <div className="ms-auto d-grid d-md-flex align-items-center justify-content-end py-2">
                <div className="d-flex align-items-center order-md-2">
                    {isLoggedIn ?
                        <div>
                            {isStudent && studentInfo &&
                                <ToolTip title={`Logged in as ${studentInfo.name}`} placement="bottom">
                                    <div className="d-inline-flex align-items-center"> 
                                        <div className="position-relative me-1">
                                            <FontAwesomeIcon icon={faUserAstronaut} size="2x" className="text-primary align-middle" />
                                            <span
                                                className={`position-absolute top-0 start-100 translate-middle p-1 bg-success border border-white rounded-circle ${styles.twinkle}`}
                                                style={{ width: "10px", height: "10px" }}
                                            />
                                        </div>
                                        <span
                                        className="text-truncate fw-bold text-primary pe-none"
                                        style={{ maxWidth: "100px" }}
                                        title={studentInfo.name}>
                                            {studentInfo.name}
                                        </span>
                                    </div>
                                </ToolTip>}
                            <button className="btn btn-default navbar-btn ms-3" onClick={logOut}>
                                Sign Out
                            </button>
                        </div>:
                        <div className="d-flex align-items-center">
                            <GuardedAnchor
                                href="/account/log-in" 
                                id="log-in-button"
                                shouldGuard={shouldGuardLinks} 
                                className="btn btn-default navbar-btn ms-2 me-3 me-sm-0">
                                Sign In
                            </GuardedAnchor>
                        </div>
                    }
                    <span className="fa fa-globe px-3 text-primary fs-5" onClick={() => setShowTranslateWidget(!showTranslateWidget)}></span>
                    <TranslateWidget show={showTranslateWidget} />
                </div>
                

                
            </div>
        </nav>
        <div role="region" className="container-fluid max-width padding-header d-print-none">
            <div className="row">
                { params.error &&
                    <div className="alert alert-danger">{params.error}</div>
                }
                { params.success &&
                    <div className="alert alert-success">{params.success}</div>
                }
                { params.info &&
                    <div className="alert alert-info">{params.info}</div>
                }
            </div>
        </div>
    </div>
}
